import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '@pb/shared';
import { endSession, listGrants, listSessions, revokeGrant } from '../api/auth';
import { Breadcrumbs } from '../components/Nav';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';

export function AccountPage() {
  const { me, setMe } = useSession();
  const navigate = useNavigate();
  const sessions = useResource(listSessions, []);
  const grants = useResource(listGrants, []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const end = async (id: number, current: boolean) => {
    setBusy(`s${id}`);
    setError(null);
    try {
      await endSession(id);
      if (current) {
        setMe(null);
        navigate('/login', { replace: true });
        return;
      }
      await sessions.refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (id: number) => {
    setBusy(`g${id}`);
    setError(null);
    try {
      await revokeGrant(id);
      await grants.refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="section">
      <Breadcrumbs items={[{ label: 'Projects', to: '/' }, { label: 'Account' }]} />
      <div className="page-header">
        <div className="page-title">
          <h1>Account</h1>
          {me ? (
            <p className="muted">
              {me.display_name} · {me.email} · {me.client_name}
            </p>
          ) : null}
        </div>
      </div>
      <ErrorAlert error={error} />
      <section className="panel">
        <div className="panel-title">
          <h2>Sessions</h2>
        </div>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Browsers where you are signed in. End any session you do not recognise.
        </p>
        {sessions.error ? (
          <ErrorState error={sessions.error} onRetry={() => void sessions.reload()} />
        ) : sessions.data === undefined ? (
          <Loading />
        ) : sessions.data.length === 0 ? (
          <EmptyState title="No live sessions" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Signed in</th>
                <th>Last seen</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((s) => (
                <tr key={s.id}>
                  <td>
                    {formatDateTime(s.created_at)} {s.current ? <span className="badge badge-accent">This browser</span> : null}
                  </td>
                  <td>{formatDateTime(s.last_seen_at)}</td>
                  <td>{formatDateTime(s.expires_at)}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void end(s.id, s.current)}
                      disabled={busy !== null}
                    >
                      {s.current ? 'End and sign out' : 'End session'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="panel">
        <div className="panel-title">
          <h2>MCP access</h2>
        </div>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Agents you allowed to act as you through MCP. Revoking stops their access immediately.
        </p>
        {grants.error ? (
          <ErrorState error={grants.error} onRetry={() => void grants.reload()} />
        ) : grants.data === undefined ? (
          <Loading />
        ) : grants.data.length === 0 ? (
          <EmptyState title="No MCP grants">When you connect an agent through MCP and approve it, it appears here.</EmptyState>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Access</th>
                <th>Granted</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {grants.data.map((g) => (
                <tr key={g.id}>
                  <td>{g.client_name}</td>
                  <td>{g.scopes.includes('write') ? 'Read and change' : 'Read only'}</td>
                  <td>{formatDateTime(g.created_at)}</td>
                  <td>{g.last_used_at ? formatDateTime(g.last_used_at) : <span className="faint">Never</span>}</td>
                  <td className="table-actions">
                    <button type="button" className="btn btn-sm" onClick={() => void revoke(g.id)} disabled={busy !== null}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
